/**
 * StudioStatusBar — one-line workstation telemetry with overlay drawers.
 *
 * Fit/reservation and takeoff details open upward over the canvas rather than
 * increasing the Studio's document height. This keeps the viewport stable
 * while users compare engineering output.
 */
import React from 'react';
import { ChevronUp } from 'lucide-react';

import type { GenerationParcelTestFitResult } from '../../../../lib/generationImpact/generationLayoutGeometry';
import { computeGenerationLayoutTakeoffs } from '../../../../lib/generationImpact/generationLayoutTakeoffs';
import { summarizeLayoutYield } from '../../../../lib/generationImpact/generationLayoutYield';
import { fitReservationRows } from './studioVerify';

export type StudioSaveState = 'saved' | 'saving' | 'unsaved' | 'error';

export interface StudioStatusBarProps {
  testFit: GenerationParcelTestFitResult;
  takeoffsOpen: boolean;
  onToggleTakeoffs: () => void;
  fitOpen: boolean;
  onToggleFit: () => void;
  activeToolLabel?: string;
  zoomPercent?: number;
  cursorPositionM?: { xM: number; yM: number } | null;
  selectedCount?: number;
  saveState?: StudioSaveState;
  /** Embedded previews remain visual-only so the host owns live announcements. */
  announceFitStatus?: boolean;
}

const SAVE_LABELS: Record<StudioSaveState, string> = {
  saved: 'Saved',
  saving: 'Saving…',
  unsaved: 'Unsaved changes',
  error: 'Save failed',
};

const SAVE_CLASSES: Record<StudioSaveState, string> = {
  saved: 'text-success',
  saving: 'text-text-secondary',
  unsaved: 'text-warning',
  error: 'text-danger',
};

export const StudioStatusBar: React.FC<StudioStatusBarProps> = ({
  testFit,
  takeoffsOpen,
  onToggleTakeoffs,
  fitOpen,
  onToggleFit,
  activeToolLabel,
  zoomPercent,
  cursorPositionM,
  selectedCount,
  saveState,
  announceFitStatus = true,
}) => {
  const yieldSummary = summarizeLayoutYield(testFit);
  const takeoffs = computeGenerationLayoutTakeoffs(testFit);
  const fitRows = fitReservationRows(testFit);
  const drawerOpen = fitOpen || (takeoffsOpen && takeoffs.status === 'available');

  return (
    <div
      className="relative z-30 shrink-0 border-t border-border bg-card"
      data-testid="studio-status-bar"
    >
      {drawerOpen && (
        <section
          className="absolute inset-x-0 bottom-full z-40 max-h-[min(35dvh,20rem)] overflow-y-auto border-y border-border bg-card shadow-[0_-14px_32px_rgba(0,0,0,0.35)]"
          aria-label={fitOpen ? 'Fit and reservation details' : 'Layout takeoffs'}
          data-testid="studio-status-drawer"
        >
          {fitOpen && (
            <dl
              id="studio-fit-reservations"
              className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border text-[10px] sm:grid-cols-3 lg:grid-cols-4"
              data-testid="studio-fit-reservations"
            >
              {fitRows.map(row => (
                <div key={row.label} className="bg-background p-2.5">
                  <dt className="text-text-muted">{row.label}</dt>
                  <dd className={`mt-1 font-bold tabular-nums ${row.danger ? 'text-danger' : 'text-text-primary'}`}>
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {takeoffsOpen && takeoffs.status === 'available' && (
            <div id="generation-layout-takeoffs" data-testid="generation-layout-takeoffs">
              <table className="w-full text-left text-[10px]">
                <caption className="sr-only">Layout quantity takeoffs</caption>
                <tbody>
                  {takeoffs.lines.map(line => (
                    <tr key={line.id} className="border-t border-border/60 first:border-t-0">
                      <th scope="row" className="px-3 py-2 font-normal text-text-secondary">{line.label}</th>
                      <td className="px-3 py-2 text-right font-bold tabular-nums text-text-primary">
                        {line.unit === 'count'
                          ? line.quantity
                          : line.unit === 'm'
                            ? `${line.quantity.toLocaleString('en-US')} m`
                            : `${line.quantity.toFixed(2)} ac`}
                      </td>
                      <td className="px-3 py-2 text-right text-[9px] text-text-muted">{line.basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="border-t border-border px-3 py-2 text-[9px] leading-relaxed text-text-muted">
            Screening geometry only. Containment and the entered edge setback are checked against the selected
            boundary; routing connectivity, grading, drainage, fire access and constructability remain external.
          </p>
        </section>
      )}

      <div
        className="overflow-x-auto"
        data-testid="generation-layout-yield-strip"
      >
        <div className="flex min-h-9 min-w-max items-center gap-3 px-2.5 text-[10px] tabular-nums text-text-secondary">
          <span
            className={`border px-2 py-0.5 font-bold uppercase tracking-wider ${
              testFit.status === 'fits'
                ? 'border-success/50 bg-success/10 text-success'
                : testFit.status === 'partial'
                  ? 'border-danger/50 bg-danger/10 text-danger'
                  : 'border-warning/50 bg-warning/10 text-warning'
            } rounded-md`}
            role={announceFitStatus ? 'status' : undefined}
          >
            {testFit.status === 'fits' ? 'Contained + setback' : testFit.status === 'partial' ? 'Fit issue' : 'Unverified'}
          </span>
          {activeToolLabel && (
            <span data-testid="studio-status-tool">
              Tool <strong className="text-text-primary">{activeToolLabel}</strong>
            </span>
          )}
          {cursorPositionM && (
            <span data-testid="studio-status-cursor">
              X <strong>{cursorPositionM.xM.toFixed(1)} m</strong>
              {' · '}
              Y <strong>{cursorPositionM.yM.toFixed(1)} m</strong>
            </span>
          )}
          {zoomPercent != null && Number.isFinite(zoomPercent) && (
            <span data-testid="studio-status-zoom">
              Zoom <strong>{Math.round(zoomPercent)}%</strong>
            </span>
          )}
          {selectedCount != null && (
            <span data-testid="studio-status-selection">
              Selected <strong>{selectedCount}</strong>
            </span>
          )}
          {testFit.containmentPct != null && (
            <span>Containment <strong>{testFit.containmentPct.toFixed(1)}%</strong></span>
          )}
          <span>Blocks <strong>{yieldSummary.blocksContained}/{yieldSummary.blocksPlaced}</strong></span>
          {yieldSummary.areaUtilizationPct != null && (
            <span>Packing <strong>{yieldSummary.areaUtilizationPct.toFixed(1)}%</strong></span>
          )}
          {yieldSummary.greenReservePct != null && (
            <span>Green reserve <strong>{yieldSummary.greenReservePct.toFixed(1)}%</strong></span>
          )}
          {yieldSummary.excludedAcres != null && (
            <span>Excluded <strong>{yieldSummary.excludedAcres.toFixed(1)} ac</strong></span>
          )}
          {testFit.obstacleOverlapAcres != null && testFit.obstacleOverlapAcres > 0.01 && (
            <span className="text-danger">
              Component overlap <strong>{testFit.obstacleOverlapAcres.toFixed(2)} ac</strong>
            </span>
          )}
          {yieldSummary.schemesCompared.length > 1 && (
            <span>
              Scheme <strong>{yieldSummary.schemesCompared[0] === 'road-rows' ? 'Road rows' : 'Compact'}</strong>
              {' · '}
              {yieldSummary.schemesCompared.length} compared
            </span>
          )}
          {saveState && (
            <span
              className={`font-semibold ${SAVE_CLASSES[saveState]}`}
              role={saveState === 'error' ? 'alert' : undefined}
              data-testid="studio-status-save"
            >
              {SAVE_LABELS[saveState]}
            </span>
          )}

          <div className="ml-auto flex items-center gap-1.5 pl-2">
            <button
              type="button"
              onClick={onToggleFit}
              className="flex min-h-7 items-center gap-1 border border-border px-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary transition-colors hover:bg-background hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary max-[599px]:min-h-11 rounded-md"
              aria-expanded={fitOpen}
              aria-controls="studio-fit-reservations"
              data-testid="studio-fit-toggle"
            >
              Fit details
              <ChevronUp className={`h-3 w-3 transition-transform ${fitOpen ? '' : 'rotate-180'}`} aria-hidden />
            </button>
            {takeoffs.status === 'available' && (
              <button
                type="button"
                onClick={onToggleTakeoffs}
                className="flex min-h-7 items-center gap-1 border border-border px-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary transition-colors hover:bg-background hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary max-[599px]:min-h-11 rounded-md"
                aria-expanded={takeoffsOpen}
                aria-controls="generation-layout-takeoffs"
                data-testid="studio-takeoffs-toggle"
              >
                Takeoffs
                <ChevronUp className={`h-3 w-3 transition-transform ${takeoffsOpen ? '' : 'rotate-180'}`} aria-hidden />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
